import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface AnonymousUser {
  id: string
  sessionId: string
  preferences: {
    location?: string
    categories: string[]
    priceRange?: string
    sortBy: string
  }
  browsingHistory: {
    businesses: string[]
    searches: string[]
    categories: string[]
  }
  favorites: string[]
  createdAt: Date
  lastActive: Date
}

interface AnonymousContextType {
  anonymousUser: AnonymousUser | null
  isAnonymous: boolean
  createAnonymousSession: () => void
  updatePreferences: (preferences: Partial<AnonymousUser['preferences']>) => void
  addToHistory: (type: 'business' | 'search' | 'category', id: string) => void
  toggleFavorite: (businessId: string) => void
  clearHistory: () => void
  clearFavorites: () => void
  getRecommendations: () => string[]
  trackEvent: (event: string, data?: any) => void
}

const AnonymousContext = createContext<AnonymousContextType | undefined>(undefined)

interface AnonymousProviderProps {
  children: ReactNode
}

export function AnonymousProvider({ children }: AnonymousProviderProps) {
  const [anonymousUser, setAnonymousUser] = useState<AnonymousUser | null>(null)
  const [isAnonymous, setIsAnonymous] = useState(false)
  
  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = () => {
      const token = localStorage.getItem('authToken')
      if (token) {
        setIsAnonymous(false)
        return
      }
      
      // Check for existing anonymous session
      const anonymousSession = localStorage.getItem('anonymousSession')
      if (anonymousSession) {
        try {
          const user = JSON.parse(anonymousSession)
          setAnonymousUser(user)
          setIsAnonymous(true)
        } catch (error) {
          console.error('Error parsing anonymous session:', error)
          localStorage.removeItem('anonymousSession')
        }
      } else {
        // Create new anonymous session
        createAnonymousSession()
      }
    }
    
    checkAuth()
  }, [])
  
  const createAnonymousSession = () => {
    const sessionId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const newUser: AnonymousUser = {
      id: sessionId,
      sessionId,
      preferences: {
        categories: [],
        sortBy: 'relevance'
      },
      browsingHistory: {
        businesses: [],
        searches: [],
        categories: []
      },
      favorites: [],
      createdAt: new Date(),
      lastActive: new Date()
    }
    
    setAnonymousUser(newUser)
    setIsAnonymous(true)
    localStorage.setItem('anonymousSession', JSON.stringify(newUser))
    
    // Track anonymous session creation
    trackEvent('anonymous_session_created', { sessionId })
  }
  
  const updatePreferences = (preferences: Partial<AnonymousUser['preferences']>) => {
    if (!anonymousUser) return
    
    const updatedUser = {
      ...anonymousUser,
      preferences: {
        ...anonymousUser.preferences,
        ...preferences
      },
      lastActive: new Date()
    }
    
    setAnonymousUser(updatedUser)
    localStorage.setItem('anonymousSession', JSON.stringify(updatedUser))
    
    trackEvent('preferences_updated', preferences)
  }
  
  const addToHistory = (type: 'business' | 'search' | 'category', id: string) => {
    if (!anonymousUser) return
    
    const updatedUser = {
      ...anonymousUser,
      browsingHistory: {
        ...anonymousUser.browsingHistory,
        [type === 'business' ? 'businesses' : type === 'search' ? 'searches' : 'categories']: [
          ...anonymousUser.browsingHistory[type === 'business' ? 'businesses' : type === 'search' ? 'searches' : 'categories'],
          id
        ].slice(-50) // Keep only last 50 items
      },
      lastActive: new Date()
    }
    
    setAnonymousUser(updatedUser)
    localStorage.setItem('anonymousSession', JSON.stringify(updatedUser))
    
    trackEvent('history_updated', { type, id })
  }
  
  const toggleFavorite = (businessId: string) => {
    if (!anonymousUser) return
    
    const updatedFavorites = anonymousUser.favorites.includes(businessId)
      ? anonymousUser.favorites.filter(id => id !== businessId)
      : [...anonymousUser.favorites, businessId]
    
    const updatedUser = {
      ...anonymousUser,
      favorites: updatedFavorites,
      lastActive: new Date()
    }
    
    setAnonymousUser(updatedUser)
    localStorage.setItem('anonymousSession', JSON.stringify(updatedUser))
    
    trackEvent('favorite_toggled', { 
      businessId, 
      action: updatedFavorites.includes(businessId) ? 'added' : 'removed' 
    })
  }
  
  const clearHistory = () => {
    if (!anonymousUser) return
    
    const updatedUser = {
      ...anonymousUser,
      browsingHistory: {
        businesses: [],
        searches: [],
        categories: []
      },
      lastActive: new Date()
    }
    
    setAnonymousUser(updatedUser)
    localStorage.setItem('anonymousSession', JSON.stringify(updatedUser))
    
    trackEvent('history_cleared')
  }
  
  const clearFavorites = () => {
    if (!anonymousUser) return
    
    const updatedUser = {
      ...anonymousUser,
      favorites: [],
      lastActive: new Date()
    }
    
    setAnonymousUser(updatedUser)
    localStorage.setItem('anonymousSession', JSON.stringify(updatedUser))
    
    trackEvent('favorites_cleared')
  }
  
  const getRecommendations = () => {
    if (!anonymousUser) return []
    
    const { browsingHistory, preferences } = anonymousUser
    
    // Simple recommendation algorithm based on browsing history
    const recommendations: string[] = []
    
    // Add categories from browsing history
    recommendations.push(...browsingHistory.categories)
    
    // Add preferred categories
    recommendations.push(...preferences.categories)
    
    // Remove duplicates and return
    return [...new Set(recommendations)]
  }
  
  const trackEvent = (event: string, data?: any) => {
    // Track anonymous user events
    console.log('Anonymous event:', event, data)
    
    // Here you would typically send to analytics service
    // analyticsService.track(event, { ...data, anonymous: true, sessionId: anonymousUser?.sessionId })
  }
  
  const value: AnonymousContextType = {
    anonymousUser,
    isAnonymous,
    createAnonymousSession,
    updatePreferences,
    addToHistory,
    toggleFavorite,
    clearHistory,
    clearFavorites,
    getRecommendations,
    trackEvent
  }
  
  return (
    <AnonymousContext.Provider value={value}>
      {children}
    </AnonymousContext.Provider>
  )
}

export function useAnonymous() {
  const context = useContext(AnonymousContext)
  if (context === undefined) {
    throw new Error('useAnonymous must be used within an AnonymousProvider')
  }
  return context
}
